import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useField } from "formik";
import { v4 as uuid } from "uuid";
import { RuleGroupType } from "@common/types/transactionRule";
import { Category } from "@common/types/category";
import { Entity } from "@common/types/entity";
import { Button } from "@design-system/Button/Button";
import { useWorkspaceRuleGroupsList } from "@api/rules.api";
import { PlayIcon, PlusIcon } from "@icons";
import styles from "../RuleForm.module.scss";
import {
    AccountingRuleFormActions,
    BusinessRuleFormActions,
    FormRuleActionType,
    TransactionRuleActionFormSchema,
    TransactionRuleFormSchema,
} from "../constants";
import { RuleActionForm } from "./RuleActionForm";
import { RuleSplitForm } from "./RuleSplitForm";
import { SplitRuleTypeForm } from "./SplitRuleTypeForm";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useTypedFlags } from "@hooks/useTypedFlags";

interface RuleActionsFormProps {
    entities: Entity[];
    entitiesById: Map<number, Entity>;
    categoriesById: Record<string, Category>;
}

export const RuleActionsForm = ({
    entities,
    entitiesById,
    categoriesById,
}: RuleActionsFormProps) => {
    const ruleGroupsList = useWorkspaceRuleGroupsList();
    const [{ value: actions }, , { setValue: setActions }] =
        useField<TransactionRuleActionFormSchema[]>("actions");
    const [{ value: groupId }] =
        useField<TransactionRuleFormSchema["groupId"]>("groupId");
    const { schedules: isSchedulesEnabled } = useTypedFlags();

    const addAction = useCallback(() => {
        setActions([...actions, { id: uuid(), transferDirection: null }]);
    }, [actions, setActions]);

    const removeAction = useCallback(
        (index: number) => {
            setActions(actions.filter((_, i) => i !== index));
        },
        [actions, setActions],
    );

    const group = useMemo(
        () => ruleGroupsList.data?.find((g) => g.id === groupId),
        [ruleGroupsList.data, groupId],
    );

    // At the moment we support 2 different actions, and you can not add duplicates.
    const canAddAction = useMemo(() => {
        if (!group) {
            return false;
        }

        if (group.type === RuleGroupType.ACCOUNTING) {
            return AccountingRuleFormActions.some(
                (actionType) =>
                    !actions.some((action) => action.actionType === actionType),
            );
        }

        return BusinessRuleFormActions.filter(
            (actionType) =>
                isSchedulesEnabled ||
                actionType !== FormRuleActionType.ASSIGN_TO_SCHEDULE,
        ).some(
            (actionType) =>
                !actions.some((action) => action.actionType === actionType),
        );
    }, [actions, group, isSchedulesEnabled]);

    return (
        <section className={styles.actions}>
            <header className={styles.header}>
                <div className={styles.group}>
                    <aside
                        className={classNames(
                            styles.sectionIcon,
                            styles.sectionIconActions,
                        )}
                    >
                        <PlayIcon className="icon-color-blue-600" />
                    </aside>
                    <span className={styles.sectionTitle}>Action</span>
                </div>
                {group?.type === RuleGroupType.SPLIT && (
                    <SplitRuleTypeForm index={0} />
                )}
            </header>

            <main className={styles.content}>
                <div className={styles.sectionRows}>
                    {group?.type === RuleGroupType.SPLIT && (
                        <RuleSplitForm
                            index={0}
                            entities={entities}
                            entitiesById={entitiesById}
                            categoriesById={categoriesById}
                        />
                    )}

                    {group?.type !== RuleGroupType.SPLIT && (
                        <>
                            {actions.map((action, index) => (
                                <div
                                    className={styles.sectionRow}
                                    key={action.id}
                                >
                                    <RuleActionForm
                                        index={index}
                                        onRemove={removeAction}
                                    />
                                </div>
                            ))}
                            <div className={styles.sectionRow}>
                                <Tooltip
                                    content={
                                        canAddAction
                                            ? undefined
                                            : "You can add only one action per type"
                                    }
                                >
                                    <Button
                                        variant="primary"
                                        size="md"
                                        leftIcon={<PlusIcon />}
                                        onClick={addAction}
                                        type="button"
                                        disabled={!canAddAction}
                                    >
                                        Add
                                    </Button>
                                </Tooltip>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </section>
    );
};
